import { useEffect, useRef } from "react";
import { highlightCode, type TokenKind } from "@/lib/code-highlight";

const TOKEN_CLASS: Record<TokenKind, string> = {
  comment: "text-[#5d7388] italic",
  string: "text-[#9ddc8a]",
  number: "text-[#f0a97f]",
  keyword: "text-[#c79bf2]",
  type: "text-[#f2c96b]",
  fn: "text-[#7fb3ff]",
  tag: "text-[#6fd8c8]",
  punct: "text-[#7f93a8]",
  plain: "text-[#cfdae6]",
};

type SyntaxCodeProps = {
  code: string;
  startLine?: number;
  highlightRange?: { start: number; end: number };
};

export function SyntaxCode(props: SyntaxCodeProps) {
  const code = props.code;
  const rangeStart = props.highlightRange?.start;
  const lines = highlightCode(code);
  const startLine = props.startLine ?? 1;
  const focus = useRef<HTMLDivElement>(null);

  // 切到“所在文件”时直接落到该步骤的代码区间，免去手动滚动。
  useEffect(() => {
    if (rangeStart === undefined) return;
    focus.current?.scrollIntoView({ block: "center", inline: "nearest" });
  }, [rangeStart, code]);

  return (
    <div className="font-mono text-[12px] leading-[1.7]">
      {lines.map((tokens, index) => {
        const lineNumber = startLine + index;
        const marked = props.highlightRange
          ? lineNumber >= props.highlightRange.start && lineNumber <= props.highlightRange.end
          : false;
        const isFocusLine = lineNumber === rangeStart;

        return (
          <div
            key={index}
            ref={isFocusLine ? focus : undefined}
            className={`flex border-l-2 ${
              marked ? "border-[#37d6a0] bg-[#37d6a0]/[0.06]" : "border-transparent"
            }`}
          >
            <span className="w-12 shrink-0 select-none border-r border-[#16222e] pr-3 text-right text-[11px] text-[#3f5266]">
              {lineNumber}
            </span>
            <span className="min-w-0 whitespace-pre-wrap break-words pl-3.5 pr-3">
              {tokens.length === 0 ? (
                <span> </span>
              ) : (
                tokens.map((token, tokenIndex) => (
                  <span key={tokenIndex} className={TOKEN_CLASS[token.kind]}>
                    {token.text}
                  </span>
                ))
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}
